import { cn } from '@/lib/utils';
import type { UpdateVacancyInput, VacancyDetail } from '@/types/recruitment';
import { useState } from 'react';
import { VacancyApplicationFormTab } from './vacancy-application-form-tab';
import { VacancyCycleTab } from './vacancy-cycle-tab';
import { VacancyDescriptionTab } from './vacancy-description-tab';
import { VacancyDetailsTab } from './vacancy-details-tab';

type TabId = 'cycle' | 'details' | 'description' | 'application-form';

const TABS: { id: TabId; label: string }[] = [
  { id: 'cycle', label: 'Cycle' },
  { id: 'details', label: 'Details' },
  { id: 'description', label: 'Description' },
  { id: 'application-form', label: 'Application Form' },
];

interface VacancyConstructorProps {
  vacancy: VacancyDetail;
  onSave: (data: UpdateVacancyInput) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

export function VacancyConstructor({ vacancy, onSave, onCancel, isSaving }: VacancyConstructorProps) {
  const [activeTab, setActiveTab] = useState<TabId>(() =>
    vacancy.status === 'DRAFT' ? 'details' : 'cycle',
  );

  const isCycle = activeTab === 'cycle';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Horizontal Tabs */}
      <div className="shrink-0 border-b border-border px-6">
        <nav className="flex gap-6" aria-label="Vacancy sections">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={cn(
                'relative pb-3 text-sm font-medium transition-colors cursor-pointer',
                activeTab === id
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
              {activeTab === id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-hygge rounded-t-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {isCycle ? (
        <VacancyCycleTab vacancy={vacancy} />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">
          {activeTab === 'details' && (
            <VacancyDetailsTab
              vacancy={vacancy}
              onSave={onSave}
              onCancel={onCancel}
              isSaving={isSaving}
            />
          )}
          {activeTab === 'description' && <VacancyDescriptionTab vacancy={vacancy} />}
          {activeTab === 'application-form' && (
            <VacancyApplicationFormTab vacancy={vacancy} />
          )}
        </div>
      )}
    </div>
  );
}
